import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { Database, Plus, Pencil, Trash2, Loader2, X, CheckCircle2, ShieldCheck, Lock, AlertTriangle, ArrowRight, Download, Printer, Camera, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";

const KINDS = [
  { id: "trade", label: "Trades", hint: "Mason, Carpenter, Electrician…" },
  { id: "contractor_type", label: "Contractor Types", hint: "Labour, Sub, Specialist…" },
  { id: "ncc_site", label: "NCC Sites", hint: "Active NCC project sites" },
  { id: "id_proof_type", label: "ID Proof Types", hint: "PAN, Aadhar, Driving Licence…" },
];

export default function AdminMastersPage({ embedded = false }) {
  const { user, loading, can } = useAuth();
  const [kind, setKind] = useState(KINDS[0].id);

  if (loading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!can("manage_masters")) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-20 text-center">
        <div className="border-2 border-slate-900 bg-white p-8 shadow-brutal">
          <Lock className="mx-auto h-10 w-10 text-slate-700" />
          <h1 className="mt-4 font-heading text-2xl font-black uppercase">Access denied</h1>
          <p className="mt-2 text-slate-600">You don&apos;t have the <span className="font-mono">manage_masters</span> permission.</p>
          <Link to="/admin" className="mt-5 inline-block border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  const current = KINDS.find((k) => k.id === kind);

  const Wrapper = embedded ? "div" : "div";
  const wrapperClass = embedded ? "" : "mx-auto max-w-7xl px-4 py-10 md:px-8";

  return (
    <Wrapper className={wrapperClass}>
      {!embedded && (
        <>
          <div className="industrial-label text-ncc-gold2">// Admin · Masters</div>
          <h1 className="mt-2 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">Data Masters</h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Manage the lookup lists used across registration, search, and admin. Changes apply instantly across the app.
          </p>
        </>
      )}

      <div className={`${embedded ? "" : "mt-8 "}grid gap-6 md:grid-cols-12`}>
        {/* Side tabs */}
        <aside className="md:col-span-3">
          <div className="border-2 border-slate-900 bg-white">
            <div className="flex items-center gap-2 border-b-2 border-slate-900 bg-ncc-navy px-4 py-3 text-white">
              <Database className="h-4 w-4 text-ncc-gold" />
              <div className="industrial-label">Master Lists</div>
            </div>
            <div className="divide-y-2 divide-slate-200">
              {KINDS.map((k) => (
                <button
                  key={k.id}
                  type="button"
                  onClick={() => setKind(k.id)}
                  data-testid={`masters-tab-${k.id}`}
                  className={`block w-full text-left px-4 py-3 transition-colors ${kind === k.id ? "bg-ncc-gold text-slate-900" : "hover:bg-slate-50"}`}
                >
                  <div className="industrial-label">{k.label}</div>
                  <div className="mt-0.5 font-mono text-xs text-slate-600">{k.hint}</div>
                </button>
              ))}
            </div>
          </div>
        </aside>

        {/* Items panel */}
        <main className="md:col-span-9">
          {kind === "trade" && <OrphanTradesPanel />}
          <MasterPanel key={kind} kind={current} />
        </main>
      </div>
    </Wrapper>
  );
}

function MasterPanel({ kind }) {
  const isSite = kind.id === "ncc_site";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", sort_order: 0, is_active: true, client_name: "", client_logo: "", site_code: "", id_seq_start: 14846 });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function load() {
    setLoading(true);
    try {
      const r = await api.get(`/masters/${kind.id}`);
      setItems(r.data);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/masters/${kind.id}`);
        if (!cancelled) setItems(r.data);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [kind.id]);

  function openCreate() {
    setEditing(null);
    setForm({ name: "", sort_order: items.length, is_active: true, client_name: "", client_logo: "", site_code: "", id_seq_start: 14846 });
    setError("");
    setShowForm(true);
  }
  function openEdit(it) {
    setEditing(it);
    setForm({
      name: it.name,
      sort_order: it.sort_order,
      is_active: it.is_active,
      client_name: it.client_name || "",
      client_logo: it.client_logo || "",
      site_code: it.site_code || "",
      id_seq_start: it.id_seq_start ?? 14846,
    });
    setError("");
    setShowForm(true);
  }

  async function onLogoFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 1 * 1024 * 1024) {
      setError("Logo too large (max 1 MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setForm((p) => ({ ...p, client_logo: reader.result }));
    reader.onerror = () => setError("Could not read logo");
    reader.readAsDataURL(f);
  }

  async function save(e) {
    e.preventDefault();
    setError("");
    if (!form.name.trim()) { setError("Name is required"); return; }
    setSaving(true);
    try {
      const payload = {
        name: form.name,
        sort_order: form.sort_order,
        is_active: form.is_active,
      };
      if (isSite) {
        payload.client_name = form.client_name || null;
        payload.client_logo = form.client_logo || "";
        payload.site_code = (form.site_code || "").trim().toUpperCase() || null;
        payload.id_seq_start = Number(form.id_seq_start) || 14846;
      }
      if (editing) {
        await api.put(`/masters/${kind.id}/${editing.id}`, payload);
      } else {
        await api.post(`/masters/${kind.id}`, payload);
      }
      setShowForm(false);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  async function del(it) {
    if (it.in_use_count > 0) {
      if (!window.confirm(`"${it.name}" is used by ${it.in_use_count} contractor(s). Delete anyway? Existing contractor records will keep the value but it will no longer appear in dropdowns.`)) return;
      try {
        await api.delete(`/masters/${kind.id}/${it.id}?force=true`);
      } catch (err) { alert(err?.response?.data?.detail || "Delete failed"); return; }
    } else {
      if (!window.confirm(`Delete "${it.name}"?`)) return;
      try {
        await api.delete(`/masters/${kind.id}/${it.id}`);
      } catch (err) { alert(err?.response?.data?.detail || "Delete failed"); return; }
    }
    await load();
  }

  async function toggleActive(it) {
    try {
      await api.put(`/masters/${kind.id}/${it.id}`, {
        name: it.name, sort_order: it.sort_order, is_active: !it.is_active,
      });
      await load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Failed to toggle");
    }
  }

  async function exportExcel() {
    try {
      const r = await api.get(`/masters/${kind.id}/export-excel`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `masters-${kind.id}.xlsx`; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch (err) {
      alert(err?.response?.data?.detail || "Export failed");
    }
  }

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">{kind.label}</h2>
          <div className="industrial-label text-slate-500">{items.length} item(s) · {items.filter((i) => i.is_active).length} active</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={exportExcel} data-testid="master-export-excel"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-3 py-2 industrial-label transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm">
            <Download className="h-4 w-4" /> Export Excel
          </button>
          <button type="button" onClick={() => window.print()} data-testid="master-print"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm">
            <Printer className="h-4 w-4" /> Print
          </button>
          <button
            onClick={openCreate}
            data-testid="master-add-btn"
            className="inline-flex items-center gap-2 border-2 border-slate-900 bg-blueprint px-4 py-2 industrial-label text-white transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm"
          >
            <Plus className="h-4 w-4" /> Add {kind.label.replace(/s$/, "")}
          </button>
        </div>
      </div>

      <div className="mt-6 overflow-x-auto border-2 border-slate-900 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-ncc-navy text-white">
            <tr>
              {(isSite
                ? ["Sort", "Name", "Client", "Logo", "Status", "In Use", "Source", "Actions"]
                : ["Sort", "Name", "Status", "In Use", "Source", "Actions"]
              ).map((h) => (
                <th key={h} className="px-4 py-3 text-left industrial-label">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={isSite ? 8 : 6} className="p-6 text-center font-mono text-slate-500">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={isSite ? 8 : 6} className="p-6 text-center font-mono text-slate-500">No items yet — click &quot;Add&quot;.</td></tr>
            ) : items.map((it) => (
              <tr key={it.id} className="border-b border-slate-200 hover:bg-slate-50" data-testid={`master-row-${it.name}`}>
                <td className="px-4 py-3 font-mono text-slate-500">{it.sort_order}</td>
                <td className="px-4 py-3 font-heading font-bold text-slate-900">{it.name}</td>
                {isSite && (
                  <td className="px-4 py-3 font-mono text-slate-700" data-testid={`master-client-name-${it.name}`}>
                    {it.client_name || <span className="text-slate-400">—</span>}
                  </td>
                )}
                {isSite && (
                  <td className="px-4 py-3" data-testid={`master-client-logo-${it.name}`}>
                    {it.client_logo ? (
                      <img src={it.client_logo} alt={it.client_name || it.name} className="h-9 w-auto max-w-[64px] rounded border border-slate-200 bg-white object-contain" />
                    ) : (
                      <div className="grid h-9 w-9 place-items-center rounded border border-dashed border-slate-300 text-slate-300">
                        <ImageIcon className="h-3.5 w-3.5" />
                      </div>
                    )}
                  </td>
                )}
                <td className="px-4 py-3">
                  <button
                    type="button"
                    onClick={() => toggleActive(it)}
                    data-testid={`master-toggle-${it.name}`}
                    className={`border-2 border-slate-900 px-2 py-0.5 industrial-label ${it.is_active ? "bg-ncc-gold text-slate-900" : "bg-slate-100 text-slate-500"}`}
                  >
                    {it.is_active ? "Active" : "Hidden"}
                  </button>
                </td>
                <td className="px-4 py-3 font-mono">
                  {it.in_use_count > 0 ? (
                    <span className="border border-slate-900 bg-white px-1.5 py-0.5 industrial-label">{it.in_use_count}</span>
                  ) : (
                    <span className="text-slate-400">0</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  {it.is_system ? (
                    <span className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 industrial-label text-slate-500">Seed</span>
                  ) : (
                    <span className="border border-slate-900 bg-white px-1.5 py-0.5 industrial-label">Custom</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => openEdit(it)}
                      data-testid={`master-edit-${it.name}`}
                      className="border border-slate-900 bg-white p-1.5 hover:bg-slate-100"
                      title="Edit"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => del(it)}
                      data-testid={`master-delete-${it.name}`}
                      className="border border-slate-900 bg-white p-1.5 hover:bg-red-50"
                      title="Delete"
                    >
                      <Trash2 className="h-3.5 w-3.5 text-red-600" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-ncc-navy/70 p-4" onClick={() => setShowForm(false)}>
          <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal-lg" data-testid="master-form">
            <div className="flex items-center justify-between border-b-2 border-slate-900 bg-ncc-navy px-5 py-3 text-white">
              <div className="industrial-label">{editing ? `Edit ${kind.label.replace(/s$/, "")}` : `Add ${kind.label.replace(/s$/, "")}`}</div>
              <button type="button" onClick={() => setShowForm(false)}><X className="h-4 w-4" /></button>
            </div>
            <div className="space-y-4 p-5">
              <label className="block">
                <div className="industrial-label text-slate-600">Name</div>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="master-name-input"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
              </label>
              <label className="block">
                <div className="industrial-label text-slate-600">Sort Order</div>
                <input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })}
                  data-testid="master-sort-input"
                  className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                />
              </label>
              {isSite && (
                <>
                  <label className="block">
                    <div className="industrial-label text-slate-600">Name of Client</div>
                    <input
                      value={form.client_name}
                      onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                      data-testid="master-client-name-input"
                      placeholder="e.g. NCCL · Adani · Mahindra"
                      className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                    />
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="block">
                      <div className="industrial-label text-slate-600">Site Code (ID Card prefix)</div>
                      <input
                        value={form.site_code}
                        onChange={(e) => setForm({ ...form, site_code: e.target.value.toUpperCase().slice(0, 6) })}
                        data-testid="master-site-code-input"
                        placeholder="e.g. MLD"
                        className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm uppercase tracking-widest focus:border-slate-900 focus:outline-none"
                      />
                      <div className="mt-1 font-mono text-[10px] text-slate-500">2-6 letters · used in ID No. (e.g. MLD-DEC-25-14846)</div>
                    </label>
                    <label className="block">
                      <div className="industrial-label text-slate-600">ID No. Start (Sequence)</div>
                      <input
                        type="number"
                        value={form.id_seq_start}
                        onChange={(e) => setForm({ ...form, id_seq_start: e.target.value })}
                        data-testid="master-id-seq-start-input"
                        placeholder="14846"
                        className="mt-1 w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                      />
                      <div className="mt-1 font-mono text-[10px] text-slate-500">First worker gets this number; subsequent +1</div>
                    </label>
                  </div>
                  <div>
                    <div className="industrial-label text-slate-600">Client Logo</div>
                    <div className="mt-1 flex items-center gap-3 border-2 border-dashed border-slate-300 bg-slate-50 px-3 py-3">
                      <div className="grid h-14 w-14 shrink-0 place-items-center overflow-hidden rounded border-2 border-slate-300 bg-white">
                        {form.client_logo ? (
                          <img src={form.client_logo} alt="" className="h-full w-full object-contain" />
                        ) : (
                          <ImageIcon className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className="inline-flex w-fit cursor-pointer items-center gap-1 border-2 border-slate-900 bg-white px-2.5 py-1.5 industrial-label hover:bg-slate-50">
                          <Camera className="h-3.5 w-3.5" />
                          {form.client_logo ? "Replace logo" : "Upload logo"}
                          <input type="file" accept="image/*" onChange={onLogoFile} className="hidden" data-testid="master-client-logo-input" />
                        </label>
                        {form.client_logo && (
                          <button
                            type="button"
                            onClick={() => setForm((p) => ({ ...p, client_logo: "" }))}
                            data-testid="master-client-logo-remove"
                            className="w-fit text-xs text-red-600 underline"
                          >
                            Remove
                          </button>
                        )}
                        <div className="font-mono text-[10px] text-slate-500">PNG / JPG · max 1 MB</div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <label className="flex cursor-pointer items-center gap-3 border-2 border-slate-300 bg-white px-3 py-2">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  data-testid="master-active-checkbox"
                  className="h-4 w-4 accent-ncc-gold"
                />
                <div>
                  <div className="industrial-label text-slate-900">Active</div>
                  <div className="font-mono text-xs text-slate-500">Inactive items are hidden from dropdowns but kept in the database.</div>
                </div>
              </label>
              {error && <div className="border-2 border-red-500 bg-red-50 p-2 font-mono text-xs text-red-700" data-testid="master-error">{error}</div>}
            </div>
            <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-5 py-3">
              <button type="button" onClick={() => setShowForm(false)} className="border-2 border-slate-900 bg-white px-4 py-2 industrial-label">Cancel</button>
              <button type="submit" disabled={saving} data-testid="master-save-btn" className="inline-flex items-center gap-2 border-2 border-slate-900 bg-ncc-gold px-4 py-2 industrial-label">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {saving ? "Saving" : (editing ? "Save" : "Add")}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}


function OrphanTradesPanel() {
  const [orphans, setOrphans] = useState([]);
  const [activeTrades, setActiveTrades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);  // orphan name currently being remapped/removed
  const [targets, setTargets] = useState({});  // {orphanName: targetTrade}

  async function load() {
    setLoading(true);
    try {
      const [oRes, mRes] = await Promise.all([
        api.get("/orphan-trades"),
        api.get("/masters/trade"),
      ]);
      setOrphans(oRes.data || []);
      setActiveTrades((mRes.data || []).filter((m) => m.is_active).map((m) => m.name));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load orphan trades");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [oRes, mRes] = await Promise.all([
          api.get("/orphan-trades"),
          api.get("/masters/trade"),
        ]);
        if (cancelled) return;
        setOrphans(oRes.data || []);
        setActiveTrades((mRes.data || []).filter((m) => m.is_active).map((m) => m.name));
      } catch (err) {
        if (!cancelled) toast.error(err?.response?.data?.detail || "Failed to load orphan trades");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  async function remap(name) {
    const to = targets[name] || "";
    if (!to) {
      toast.error("Pick a target trade to re-map to");
      return;
    }
    if (!window.confirm(`Re-map "${name}" → "${to}" across all contractors?`)) return;
    setBusy(name);
    try {
      const r = await api.post("/orphan-trades/remap", { from: name, to });
      toast.success(`Re-mapped on ${r.data.updated} contractor(s)`);
      setTargets((t) => { const c = { ...t }; delete c[name]; return c; });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Re-map failed");
    } finally {
      setBusy(null);
    }
  }

  async function remove(name) {
    if (!window.confirm(`Remove "${name}" from every contractor (no replacement)? This cannot be undone.`)) return;
    setBusy(name);
    try {
      const r = await api.post("/orphan-trades/remap", { from: name, to: null });
      toast.success(`Removed from ${r.data.updated} contractor(s)`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Remove failed");
    } finally {
      setBusy(null);
    }
  }

  if (loading) {
    return (
      <div className="mb-6 border-2 border-slate-300 bg-slate-50 p-4 font-mono text-xs text-slate-500" data-testid="orphan-trades-loading">
        Scanning for orphan trades…
      </div>
    );
  }

  if (orphans.length === 0) {
    return (
      <div className="mb-6 flex items-center gap-3 border-2 border-emerald-500 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="orphan-trades-clean">
        <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
        <div>
          <div className="industrial-label text-emerald-700">All clean</div>
          No orphan trade values found on any contractor record.
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 border-2 border-amber-600 bg-amber-50 shadow-brutal-sm print:hidden" data-testid="orphan-trades-panel">
      <div className="flex items-center gap-2 border-b-2 border-amber-600 bg-amber-100 px-4 py-3">
        <AlertTriangle className="h-5 w-5 text-amber-700" />
        <div className="industrial-label text-amber-800">Orphan trades · {orphans.length} found</div>
        <button
          type="button"
          onClick={load}
          data-testid="orphan-trades-refresh"
          className="ml-auto border border-amber-700 bg-white px-2 py-1 industrial-label text-amber-800 hover:bg-amber-50"
        >
          Refresh
        </button>
      </div>
      <div className="px-4 pt-3 pb-1 font-mono text-xs text-amber-800">
        These trade values exist on contractor records but are NOT in the active Trades master (likely renamed or removed). Re-map each one to a valid trade, or remove it entirely.
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-amber-100">
            <tr className="border-b-2 border-amber-600">
              {["Orphan Trade", "Contractors", "Workers", "Re-map to", "Actions"].map((h) => (
                <th key={h} className="px-4 py-2 text-left industrial-label text-amber-900">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {orphans.map((o) => {
              const target = targets[o.name] || "";
              const isBusy = busy === o.name;
              return (
                <tr key={o.name} className="border-b border-amber-200" data-testid={`orphan-row-${o.name}`}>
                  <td className="px-4 py-3">
                    <div className="font-heading font-bold text-slate-900">{o.name}</div>
                    <div className="font-mono text-xs text-amber-700">Not in masters</div>
                  </td>
                  <td className="px-4 py-3 font-mono font-bold">{o.contractor_count}</td>
                  <td className="px-4 py-3 font-mono">{o.total_workers}</td>
                  <td className="px-4 py-3">
                    <select
                      value={target}
                      onChange={(e) => setTargets({ ...targets, [o.name]: e.target.value })}
                      data-testid={`orphan-target-${o.name}`}
                      disabled={isBusy}
                      className="w-full max-w-[220px] border-2 border-slate-900 bg-white px-2 py-1.5 font-mono text-sm focus:bg-slate-50 focus:outline-none"
                    >
                      <option value="">— Select active trade —</option>
                      {activeTrades.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => remap(o.name)}
                        disabled={!target || isBusy}
                        data-testid={`orphan-remap-${o.name}`}
                        className="inline-flex items-center gap-1 border-2 border-slate-900 bg-ncc-gold px-3 py-1.5 industrial-label transition-all hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50 disabled:hover:translate-y-0 disabled:hover:shadow-none"
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ArrowRight className="h-3.5 w-3.5" />}
                        Re-map
                      </button>
                      <button
                        type="button"
                        onClick={() => remove(o.name)}
                        disabled={isBusy}
                        data-testid={`orphan-remove-${o.name}`}
                        className="inline-flex items-center gap-1 border-2 border-slate-900 bg-white px-3 py-1.5 industrial-label text-red-700 transition-all hover:-translate-y-0.5 hover:bg-red-50 hover:shadow-brutal-sm disabled:opacity-50"
                      >
                        {isBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                        Remove
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
